import { ExternalLink, Laptop, Pencil, Plus, RotateCcw, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMyLaptopSystems } from "@/hooks/use-laptop-systems";
import {
  bookmarkInitials,
  type LaptopBookmark,
  laptopBookmarkSchema,
  loadLaptopBookmarks,
  newBookmarkId,
  normalizeBookmarkUrl,
  resetLaptopBookmarks,
  saveLaptopBookmarks,
} from "@/lib/office/laptop-bookmarks";
import { cn } from "@/lib/utils";

type LaptopScreenProps = {
  open: boolean;
  onClose: () => void;
  /** Plaque / desk name for chrome */
  deskName?: string;
};

/**
 * Virtual desk laptop — fullscreen bezel with app bookmarks.
 * Opens only from your personal office; links open in a new tab.
 * Workspace systems are assigned by admins; personal tiles are local-only.
 */
export function LaptopScreen({ open, onClose, deskName = "My desk" }: LaptopScreenProps) {
  const {
    data: workspaceSystems = [],
    isLoading: systemsLoading,
    isError: systemsError,
    error: systemsErr,
    refetch: refetchSystems,
  } = useMyLaptopSystems(open);
  const [bookmarks, setBookmarks] = useState<LaptopBookmark[]>(() =>
    typeof window !== "undefined" ? loadLaptopBookmarks() : [],
  );
  const [editing, setEditing] = useState(false);
  const [draftLabel, setDraftLabel] = useState("");
  const [draftUrl, setDraftUrl] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setBookmarks(loadLaptopBookmarks());
    setEditing(false);
    setEditId(null);
    setDraftError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  function persist(next: LaptopBookmark[]) {
    setBookmarks(next);
    saveLaptopBookmarks(next);
  }

  function openAdd() {
    setEditId(null);
    setDraftLabel("");
    setDraftUrl("https://");
    setDraftError(null);
    setEditing(true);
  }

  function openEdit(b: LaptopBookmark) {
    setEditId(b.id);
    setDraftLabel(b.label);
    setDraftUrl(b.url);
    setDraftError(null);
    setEditing(true);
  }

  function saveDraft() {
    const label = draftLabel.trim();
    const url = normalizeBookmarkUrl(draftUrl);
    const existing = editId ? bookmarks.find((b) => b.id === editId) : undefined;
    const candidate = {
      id: editId ?? newBookmarkId(),
      label,
      url,
      color: existing?.color ?? "var(--vy-cta)",
    };
    const parsed = laptopBookmarkSchema.safeParse(candidate);
    if (!parsed.success) {
      const msg = parsed.error.issues[0]?.message ?? "Invalid bookmark";
      setDraftError(msg);
      return;
    }
    if (editId) {
      persist(bookmarks.map((b) => (b.id === editId ? parsed.data : b)));
    } else {
      if (bookmarks.length >= 24) {
        setDraftError("Maximum 24 bookmarks");
        return;
      }
      persist([...bookmarks, parsed.data]);
    }
    setEditing(false);
    setEditId(null);
    setDraftError(null);
  }

  function removeBookmark(id: string) {
    persist(bookmarks.filter((b) => b.id !== id));
  }

  function handleReset() {
    persist(resetLaptopBookmarks());
    setEditing(false);
  }

  return (
    <div
      className="absolute inset-0 z-[56] flex items-center justify-center bg-[rgba(6,12,28,0.72)] p-3 backdrop-blur-[3px] sm:p-6"
      role="dialog"
      aria-label="Desk laptop"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Close laptop"
        onClick={onClose}
      />

      {/* Laptop chassis */}
      <div className="relative z-10 flex w-full max-w-[720px] flex-col items-center">
        <div className="w-full overflow-hidden rounded-t-2xl border border-[#2a3548] bg-[#0d1524] shadow-[0_24px_80px_rgba(0,0,0,0.55)] ring-1 ring-white/10">
          {/* Camera bezel */}
          <div className="flex items-center justify-center gap-2 border-b border-white/5 bg-[#0a101c] px-3 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[#2a3548] ring-1 ring-white/10" />
            <span className="text-[10px] font-semibold tracking-[0.14em] text-white/35 uppercase">
              Desk laptop
            </span>
          </div>

          {/* Title bar */}
          <div className="flex items-center gap-2 border-b border-white/8 bg-[#111b2e] px-3 py-2.5 sm:px-4">
            <Laptop className="h-4 w-4 shrink-0 text-[var(--vy-accent)]" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-white">{deskName}</p>
              <p className="text-[11px] text-white/50">
                Workspace systems · your personal bookmarks
              </p>
            </div>
            <button
              type="button"
              onClick={handleReset}
              className="hidden cursor-pointer items-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold text-white/55 hover:bg-white/8 hover:text-white/85 sm:inline-flex"
              title="Reset personal bookmarks only"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset personal
            </button>
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer rounded-lg p-1.5 text-white/60 hover:bg-white/10 hover:text-white"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Screen body */}
          <div className="max-h-[min(58vh,420px)] overflow-y-auto bg-gradient-to-b from-[#152238] to-[#0e1829] px-3 py-4 sm:px-5 sm:py-5">
            {editing ? (
              <div className="mx-auto max-w-md space-y-3 rounded-xl border border-white/10 bg-[#0c1424]/90 p-4">
                <p className="text-sm font-bold text-white">
                  {editId ? "Edit bookmark" : "Add bookmark"}
                </p>
                <div className="space-y-1.5">
                  <Label className="text-white/70">Name</Label>
                  <Input
                    value={draftLabel}
                    onChange={(e) => setDraftLabel(e.target.value)}
                    placeholder="e.g. Design system"
                    className="border-white/15 bg-white/5 text-white placeholder:text-white/35"
                    maxLength={40}
                    autoFocus
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-white/70">URL</Label>
                  <Input
                    value={draftUrl}
                    onChange={(e) => setDraftUrl(e.target.value)}
                    placeholder="https://…"
                    className="border-white/15 bg-white/5 text-white placeholder:text-white/35"
                    maxLength={2000}
                  />
                </div>
                {draftError ? (
                  <p className="text-xs font-medium text-[#ff8a80]">{draftError}</p>
                ) : null}
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button type="button" size="sm" onClick={saveDraft}>
                    Save
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
                    onClick={() => {
                      setEditing(false);
                      setDraftError(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                <section>
                  <h3 className="mb-2 text-[11px] font-bold tracking-[0.12em] text-white/45 uppercase">
                    Workspace systems
                  </h3>
                  {systemsLoading ? (
                    <p className="text-sm text-white/45">Loading systems…</p>
                  ) : systemsError ? (
                    <div className="rounded-lg border border-[var(--vy-bad)]/40 bg-[var(--vy-bad)]/10 px-3 py-2 text-xs text-[#ffb4ae]">
                      <p>{systemsErr?.message ?? "Failed to load workspace systems."}</p>
                      <button
                        type="button"
                        className="mt-1.5 cursor-pointer font-semibold text-white underline-offset-2 hover:underline"
                        onClick={() => void refetchSystems()}
                      >
                        Retry
                      </button>
                    </div>
                  ) : workspaceSystems.length === 0 ? (
                    <p className="rounded-lg border border-white/10 bg-white/4 px-3 py-2 text-xs text-white/50">
                      No systems assigned yet. Ask an admin to add tools under Settings → Laptop
                      systems.
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3 md:grid-cols-4">
                      {workspaceSystems.map((s) => (
                        <SystemTile
                          key={s.id}
                          name={s.name}
                          url={s.url}
                          color={s.color}
                          badge={s.isGlobal ? "All" : "You"}
                        />
                      ))}
                    </div>
                  )}
                </section>
                <section>
                  <h3 className="mb-2 text-[11px] font-bold tracking-[0.12em] text-white/45 uppercase">
                    Your bookmarks
                  </h3>
                  <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3 md:grid-cols-4">
                    {bookmarks.map((b) => (
                      <BookmarkTile
                        key={b.id}
                        bookmark={b}
                        onEdit={() => openEdit(b)}
                        onRemove={() => removeBookmark(b.id)}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={openAdd}
                      className="flex min-h-[96px] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 bg-white/4 text-white/70 transition-colors hover:border-[var(--vy-accent)]/50 hover:bg-white/8 hover:text-white"
                    >
                      <Plus className="h-6 w-6" />
                      <span className="text-xs font-semibold">Add app</span>
                    </button>
                  </div>
                </section>
              </div>
            )}
          </div>

          {/* Dock hint */}
          <div className="border-t border-white/8 bg-[#0a101c] px-4 py-2 text-center text-[10px] text-white/40">
            Opens in a new tab · Esc to close · workspace systems are set by admins
          </div>
        </div>

        {/* Laptop base / hinge */}
        <div
          className="h-2.5 w-[102%] max-w-[740px] rounded-b-md bg-gradient-to-b from-[#3a4558] to-[#1c2433]"
          style={{ clipPath: "polygon(2% 0, 98% 0, 100% 100%, 0 100%)" }}
        />
        <div className="mt-0.5 h-1.5 w-[38%] rounded-b-full bg-[#1a2230]" />
      </div>
    </div>
  );
}

function SystemTile({
  name,
  url,
  color,
  badge,
}: {
  name: string;
  url: string;
  color: string;
  badge: string;
}) {
  const initials = bookmarkInitials(name);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="relative flex min-h-[96px] flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border border-white/10 bg-[#0c1526]/80 px-2 py-3 text-center shadow-sm transition-shadow hover:border-white/20 hover:shadow-md"
    >
      <span className="absolute top-1.5 right-1.5 rounded bg-black/40 px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-white/70 uppercase">
        {badge}
      </span>
      <span
        className="flex h-11 w-11 items-center justify-center rounded-xl text-sm font-bold text-white shadow-inner ring-1 ring-white/15"
        style={{
          background: `linear-gradient(145deg, ${color}, ${shade(color, -35)})`,
          fontFamily: "var(--font-display)",
        }}
      >
        {initials}
      </span>
      <span className="line-clamp-2 text-[12px] font-semibold leading-snug text-white/90">
        {name}
      </span>
      <span className="inline-flex items-center gap-0.5 text-[9px] font-medium text-white/40">
        Open <ExternalLink className="h-2.5 w-2.5" />
      </span>
    </a>
  );
}

function BookmarkTile({
  bookmark,
  onEdit,
  onRemove,
}: {
  bookmark: LaptopBookmark;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const color = bookmark.color ?? "var(--vy-cta)";
  const initials = bookmarkInitials(bookmark.label);

  return (
    <div className="group relative flex min-h-[96px] flex-col overflow-hidden rounded-xl border border-white/10 bg-[#0c1526]/80 shadow-sm transition-shadow hover:border-white/20 hover:shadow-md">
      <a
        href={bookmark.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex flex-1 flex-col items-center justify-center gap-2 px-2 py-3 text-center"
      >
        <span
          className="flex h-11 w-11 items-center justify-center rounded-xl text-sm font-bold text-white shadow-inner ring-1 ring-white/15"
          style={{
            background: `linear-gradient(145deg, ${color}, ${shade(color, -35)})`,
            fontFamily: "var(--font-display)",
          }}
        >
          {initials}
        </span>
        <span className="line-clamp-2 text-[12px] font-semibold leading-snug text-white/90">
          {bookmark.label}
        </span>
        <span className="inline-flex items-center gap-0.5 text-[9px] font-medium text-white/40">
          Open <ExternalLink className="h-2.5 w-2.5" />
        </span>
      </a>
      <div
        className={cn(
          "absolute top-1.5 right-1.5 flex gap-0.5 opacity-0 transition-opacity",
          "group-hover:opacity-100 focus-within:opacity-100",
        )}
      >
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onEdit();
          }}
          className="cursor-pointer rounded-md bg-black/50 p-1 text-white/80 hover:bg-black/70 hover:text-white"
          title="Edit"
          aria-label={`Edit ${bookmark.label}`}
        >
          <Pencil className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onRemove();
          }}
          className="cursor-pointer rounded-md bg-black/50 p-1 text-white/80 hover:bg-[var(--vy-bad)]/90 hover:text-white"
          title="Remove"
          aria-label={`Remove ${bookmark.label}`}
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function shade(hex: string, amt: number): string {
  const h = hex.replace("#", "");
  if (h.length !== 6) return hex;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return hex;
  const r = Math.max(0, Math.min(255, ((n >> 16) & 255) + amt));
  const g = Math.max(0, Math.min(255, ((n >> 8) & 255) + amt));
  const b = Math.max(0, Math.min(255, (n & 255) + amt));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}
